import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertOctagon, Copy, Loader2, Phone, X, Printer, MessageCircleHeart, ArrowRight, Edit3, RotateCcw, Save, Volume2, VolumeX, Clock, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import BluetoothTempButton from "./BluetoothTempButton";
import VisitTracker from "./VisitTracker";
import EmergencyDisclaimer from "./EmergencyDisclaimer";
import { format, parseISO } from "date-fns";

const EMERGENCY_NUMBERS = [
  { region: "UK",      number: "999" },
  { region: "US · CA", number: "911" },
  { region: "EU",      number: "112" },
  { region: "AU",      number: "000" },
];

const SYMPTOMS = [
  { key: "red_skin",              label: "Red / inflamed skin?" },
  { key: "increased_swelling",    label: "More swollen than usual?" },
  { key: "hot_to_touch",          label: "Hot to the touch?" },
  { key: "aching",                label: "Aching?" },
  { key: "fever_or_temperature",  label: "Fever or temperature?", danger: true },
  { key: "nauseous_or_faint",     label: "Nauseous or faint?", danger: true },
  { key: "shaking_or_convulsing", label: "Shaking or convulsing?", danger: true },
];

function YesNoPill({ value, onChange, testId, danger }) {
  return (
    <div className="flex gap-1.5" data-testid={testId}>
      {[
        { v: true,  label: "Yes", cls: danger ? "bg-[#E63946] text-white border-[#E63946]" : "bg-kawaii-peach text-kawaii-text border-kawaii-peach" },
        { v: false, label: "No",  cls: "bg-kawaii-mint/60 text-kawaii-text border-kawaii-mint" },
      ].map((o) => {
        const active = value === o.v;
        return (
          <button
            type="button" key={String(o.v)}
            onClick={() => onChange(active ? null : o.v)}
            data-testid={`${testId}-${o.v ? 'yes' : 'no'}`}
            className={`kawaii-btn px-3.5 py-1 rounded-full text-xs font-body font-semibold border ${
              active ? o.cls : 'bg-white text-kawaii-textSoft border-kawaii-border'
            }`}
          >{o.label}</button>
        );
      })}
    </div>
  );
}

export default function EmergencyScript({ onClose, initialAnswers = null }) {
  const [tab, setTab] = useState("script"); // 'script' | 'timeline'
  const [step, setStep] = useState("assessment"); // 'assessment' | 'script'
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [answers, setAnswers] = useState({
    red_skin: initialAnswers?.red_skin ?? null,
    increased_swelling: initialAnswers?.increased_swelling ?? null,
    hot_to_touch: initialAnswers?.hot_to_touch ?? null,
    aching: initialAnswers?.aching ?? null,
    fever_or_temperature: initialAnswers?.fever_or_temperature ?? null,
    temperature_c: initialAnswers?.temperature_c ?? "",
    nauseous_or_faint: initialAnswers?.nauseous_or_faint ?? null,
    shaking_or_convulsing: initialAnswers?.shaking_or_convulsing ?? null,
  });
  const [beenHospitalBefore, setBeenHospitalBefore] = useState(null);
  const [hospitalAdmissions, setHospitalAdmissions] = useState("");
  const [cellulitisCount, setCellulitisCount] = useState("");
  const [generating, setGenerating] = useState(false);
  const [script, setScript] = useState("");
  const [addendum, setAddendum] = useState("");
  const [editing, setEditing] = useState(false);
  const [savingAddendum, setSavingAddendum] = useState(false);
  const [autoScript, setAutoScript] = useState("");
  const [speaking, setSpeaking] = useState(false);
  const scriptRef = useRef(null);
  const utterRef = useRef(null);

  useEffect(() => {
    api.get("/profile").then((r) => {
      setProfile(r.data);
      if (r.data.hospital_admissions_lifetime != null) {
        setHospitalAdmissions(String(r.data.hospital_admissions_lifetime));
        setBeenHospitalBefore(r.data.hospital_admissions_lifetime > 0);
      }
      if (r.data.cellulitis_count_lifetime != null) {
        setCellulitisCount(String(r.data.cellulitis_count_lifetime));
      }
      if (r.data.custom_script_addendum) setAddendum(r.data.custom_script_addendum);
    }).finally(() => setLoading(false));
  }, []);

  // Cellulitis episodes for the Timeline tab
  const [episodes, setEpisodes] = useState([]);
  const [episodesLoading, setEpisodesLoading] = useState(true);
  const [selectedEpisodeId, setSelectedEpisodeId] = useState(null);

  const loadEpisodes = async () => {
    setEpisodesLoading(true);
    try {
      const { data } = await api.get("/cellulitis");
      setEpisodes(data || []);
      const active = (data || []).find((e) => !e.end_date);
      setSelectedEpisodeId((prev) => prev || active?.id || data?.[0]?.id || null);
    } catch { /* silent — user can retry via tab click */ }
    finally { setEpisodesLoading(false); }
  };

  useEffect(() => {
    if (tab === "timeline") loadEpisodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const setAns = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const generate = async () => {
    setGenerating(true);
    try {
      const payload = { ...answers };
      payload.temperature_c = answers.temperature_c ? parseFloat(answers.temperature_c) : null;
      if (beenHospitalBefore === true && hospitalAdmissions !== "") {
        payload.hospital_admissions_lifetime = parseInt(hospitalAdmissions, 10);
      } else if (beenHospitalBefore === false) {
        payload.hospital_admissions_lifetime = 0;
      }
      if (cellulitisCount !== "") payload.cellulitis_count_lifetime = parseInt(cellulitisCount, 10);
      const { data } = await api.post("/emergency/script", payload);
      setScript(data.script);
      setAutoScript(data.script);
      setStep("script");
    } catch { toast.error("Couldn't generate script"); }
    finally { setGenerating(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(script); toast.success("Copied to clipboard ♡"); }
    catch { toast.error("Couldn't copy — press & hold to select"); }
  };

  const printPage = () => {
    const w = window.open("", "_blank");
    if (!w) return toast.error("Please allow pop-ups");

    const doc = w.document;
    doc.title = "Urgent Care Script";

    const style = doc.createElement("style");
    style.textContent = "body{font-family:Georgia,serif;max-width:640px;margin:40px auto;padding:20px;line-height:1.55;color:#222;}pre{white-space:pre-wrap;font-family:inherit;font-size:15px;}h1{font-size:18px;letter-spacing:1px;color:#E63946;border-bottom:2px solid #E63946;padding-bottom:8px;}";
    doc.head.appendChild(style);

    const h1 = doc.createElement("h1");
    h1.textContent = "URGENT CARE SCRIPT";
    doc.body.appendChild(h1);

    // textContent auto-escapes — no XSS risk here even if script contained markup
    const pre = doc.createElement("pre");
    pre.textContent = script || "";
    doc.body.appendChild(pre);

    w.print();
  };

  const askLymphie = () => {
    window.dispatchEvent(new CustomEvent("open-lymphie", { detail: {
      seedMessage: `I'm heading to urgent care right now for my cellulitis. Stay with me — what should I watch for while I wait, and what do I say if they try to send me home?`,
    }}));
  };

  const speak = () => {
    if (!("speechSynthesis" in window)) {
      toast.error("Your browser can't read aloud — sorry ♡");
      return;
    }
    window.speechSynthesis.cancel();
    // Strip the "URGENT — CELLULITIS…" header dashes and softly re-flow for clearer speech
    const spoken = script
      .replace(/—/g, ",")
      .replace(/•/g, "")
      .replace(/\n{2,}/g, ". ")
      .replace(/\n/g, " ")
      .replace(/\s{2,}/g, " ");
    const utter = new SpeechSynthesisUtterance(spoken);
    utter.rate = 0.95;
    utter.pitch = 1;
    utter.volume = 1;
    const voices = window.speechSynthesis.getVoices();
    const pref = voices.find((v) => /en-GB/i.test(v.lang) && /female|Google|Kate|Serena|Samantha/i.test(v.name))
              || voices.find((v) => /en-GB/i.test(v.lang))
              || voices.find((v) => /female|Samantha|Google/i.test(v.name))
              || voices.find((v) => /en/i.test(v.lang));
    if (pref) utter.voice = pref;
    utter.onend = () => setSpeaking(false);
    utter.onerror = () => { setSpeaking(false); toast.error("Playback stopped"); };
    utterRef.current = utter;
    window.speechSynthesis.speak(utter);
    setSpeaking(true);
  };

  const stopSpeaking = () => {
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    setSpeaking(false);
  };

  // Ensure speech stops if the modal is closed
  useEffect(() => () => { if ("speechSynthesis" in window) window.speechSynthesis.cancel(); }, []);

  const resetToAuto = () => {
    setScript(autoScript);
    setEditing(false);
    toast.success("Reset to auto-generated version");
  };

  const saveAddendum = async () => {
    setSavingAddendum(true);
    try {
      await api.put("/emergency/script/addendum", { custom_script_addendum: addendum });
      toast.success("Saved — I'll include this in future scripts ♡");
    } catch { toast.error("Couldn't save"); }
    finally { setSavingAddendum(false); }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[60] backdrop-blur-xl bg-white/40 overflow-y-auto"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="max-w-2xl mx-auto my-6 md:my-10 rounded-[2rem] overflow-hidden border border-[#E63946]/40 bg-white shadow-2xl"
        data-testid="emergency-modal"
      >
        <div className="p-5 md:p-6 bg-gradient-to-r from-[#FF6B6B] to-[#E63946] text-white flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
              <AlertOctagon className="w-6 h-6" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold opacity-90">Urgent care</div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold leading-tight">
                {step === "assessment" ? "Quick 30-second check" : "Show this to reception"}
              </h2>
              <p className="text-xs opacity-90 mt-1">
                {step === "assessment"
                  ? "Answer what you can. I'll tailor your urgent-care script."
                  : "Personalised. Editable. Copy, print, or hand over your phone."}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="close-emergency-btn"
            className="kawaii-btn w-9 h-9 rounded-full bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0"
          >
            <X className="w-4 h-4" strokeWidth={2.5} />
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex items-center gap-1 px-3 pt-3 pb-0 bg-gradient-to-b from-[#E63946]/8 to-transparent border-b border-kawaii-border">
          <button
            onClick={() => setTab("script")}
            data-testid="tab-script"
            className={`kawaii-btn text-xs px-4 py-2 rounded-t-2xl rounded-b-none font-heading font-semibold inline-flex items-center gap-2 border-b-2 transition-colors ${
              tab === "script" ? 'bg-white border-[#E63946] text-[#E63946]' : 'bg-transparent border-transparent text-kawaii-textSoft hover:text-kawaii-text'
            }`}
          >
            <FileText className="w-3.5 h-3.5" strokeWidth={2.5} /> Emergency script
          </button>
          <button
            onClick={() => setTab("timeline")}
            data-testid="tab-timeline"
            className={`kawaii-btn text-xs px-4 py-2 rounded-t-2xl rounded-b-none font-heading font-semibold inline-flex items-center gap-2 border-b-2 transition-colors ${
              tab === "timeline" ? 'bg-white border-[#E63946] text-[#E63946]' : 'bg-transparent border-transparent text-kawaii-textSoft hover:text-kawaii-text'
            }`}
          >
            <Clock className="w-3.5 h-3.5" strokeWidth={2.5} /> ER timeline
          </button>
        </div>

        <EmergencyDisclaimer />

        <div className="p-5 md:p-6">
          {loading ? (
            <div className="py-10 flex items-center justify-center gap-2 text-kawaii-textSoft">
              <Loader2 className="w-5 h-5 animate-spin" /> loading...
            </div>
          ) : tab === "timeline" ? (
            <div className="space-y-4" data-testid="timeline-pane">
              <div className="rounded-2xl bg-kawaii-bg/60 border border-kawaii-border p-4">
                <div className="text-[10px] uppercase tracking-[0.25em] text-kawaii-textSoft font-bold mb-2">Urgent-care visit timeline</div>
                <p className="text-xs text-kawaii-textSoft font-body italic mb-3">
                  Track wait times, milestones (triage → doctor → IV antibiotics → discharge), and sepsis red flags — all tied to the cellulitis episode below.
                </p>
                {episodesLoading ? (
                  <div className="py-6 flex items-center justify-center gap-2 text-kawaii-textSoft text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading your episodes...
                  </div>
                ) : episodes.length === 0 ? (
                  <div className="rounded-2xl bg-white border border-kawaii-border p-4 text-center">
                    <p className="text-sm font-body text-kawaii-text">
                      You haven't logged a cellulitis episode yet.
                    </p>
                    <p className="text-xs text-kawaii-textSoft font-body mt-1.5 italic">
                      Timelines are tied to episodes. Open the Cellulitis tracker and log the current one, then come back here.
                    </p>
                  </div>
                ) : (
                  <>
                    {episodes.length > 1 && (
                      <div className="mb-3">
                        <label className="text-[10px] uppercase tracking-[0.2em] text-kawaii-textSoft font-bold">Episode</label>
                        <select
                          value={selectedEpisodeId || ""}
                          onChange={(e) => setSelectedEpisodeId(e.target.value)}
                          data-testid="episode-picker"
                          className="mt-1 w-full px-3 py-2 rounded-2xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text focus:outline-none focus:ring-2 focus:ring-[#E63946]/60"
                        >
                          {episodes.map((ep) => {
                            const started = format(parseISO(ep.start_date), "MMM d, yyyy");
                            const status = ep.end_date ? "resolved" : "ACTIVE";
                            return (
                              <option key={ep.id} value={ep.id}>
                                {started} — {status} · {(ep.severity || "").replace("_"," ")}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    )}
                    {selectedEpisodeId && (
                      <VisitTracker key={selectedEpisodeId} episodeId={selectedEpisodeId} defaultExpanded />
                    )}
                  </>
                )}
              </div>
            </div>
          ) : step === "assessment" ? (
            <div className="space-y-4" data-testid="assessment-step">
              <div className="rounded-2xl bg-kawaii-bg/60 border border-kawaii-border p-4">
                <div className="text-[10px] uppercase tracking-[0.25em] text-kawaii-textSoft font-bold mb-2">History</div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-kawaii-text font-body">Have you been to hospital with cellulitis before?</span>
                    <YesNoPill value={beenHospitalBefore} onChange={setBeenHospitalBefore} testId="q-hosp-before" />
                  </div>
                  {beenHospitalBefore === true && (
                    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between gap-3 pl-3 border-l-2 border-[#E63946]/40">
                      <span className="text-sm text-kawaii-text font-body">How many times?</span>
                      <input
                        type="number" min="1" max="200"
                        value={hospitalAdmissions}
                        onChange={(e) => setHospitalAdmissions(e.target.value)}
                        data-testid="q-hosp-count"
                        placeholder="e.g. 3"
                        className="w-24 px-3 py-1 rounded-xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text text-center focus:outline-none focus:ring-2 focus:ring-[#E63946]/60"
                      />
                    </motion.div>
                  )}
                </div>
              </div>

              <div className="rounded-2xl bg-kawaii-bg/60 border border-kawaii-border p-4">
                <div className="text-[10px] uppercase tracking-[0.25em] text-kawaii-textSoft font-bold mb-2">Right now, are you experiencing…</div>
                <div className="space-y-2">
                  {SYMPTOMS.map((s) => (
                    <div key={s.key} className={`flex items-center justify-between gap-3 p-2.5 rounded-2xl border ${
                      answers[s.key] === true && s.danger ? 'border-[#E63946]/60 bg-[#FFE5E5]/40' : 'border-kawaii-border bg-white'
                    }`}>
                      <span className="text-sm text-kawaii-text font-body pr-2">{s.label}</span>
                      <YesNoPill value={answers[s.key]} onChange={(v) => setAns(s.key, v)} testId={`q-${s.key}`} danger={s.danger} />
                    </div>
                  ))}
                  {answers.fever_or_temperature === true && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center justify-between gap-3 p-2.5 rounded-2xl border border-[#E63946]/60 bg-[#FFE5E5]/40">
                      <span className="text-sm text-kawaii-text font-body pr-2">Temperature (°C)</span>
                      <div className="flex items-center gap-2 flex-wrap justify-end">
                        <input
                          type="number" step="0.1" min="34" max="43"
                          value={answers.temperature_c}
                          onChange={(e) => setAns("temperature_c", e.target.value)}
                          data-testid="q-temp-value"
                          placeholder="38.5"
                          className="w-24 px-3 py-1 rounded-xl border border-kawaii-border bg-white font-body text-sm text-kawaii-text text-center focus:outline-none focus:ring-2 focus:ring-[#E63946]/60"
                        />
                        <BluetoothTempButton onRead={(c) => setAns("temperature_c", c)} testId="q-bt-thermo" />
                        <button
                          type="button"
                          onClick={() => setAns("temperature_c", "")}
                          data-testid="q-temp-unknown"
                          className={`kawaii-btn text-[11px] px-2.5 py-1 rounded-full font-body font-semibold border ${
                            answers.temperature_c === "" ? 'bg-kawaii-lavender/60 border-kawaii-lavender text-kawaii-text' : 'bg-white border-kawaii-border text-kawaii-textSoft'
                          }`}
                        >Don't know</button>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>

              <button
                onClick={generate}
                disabled={generating}
                data-testid="generate-script-btn"
                className="kawaii-btn w-full py-3 rounded-full bg-gradient-to-r from-[#FF6B6B] to-[#E63946] text-white font-heading font-semibold text-sm shadow-md inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" strokeWidth={2.5} />}
                Generate my script
              </button>
              <p className="text-[11px] text-kawaii-textSoft text-center font-body italic">
                Your hospital + episode counts will be remembered for next time.
              </p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="script-step">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setStep("assessment")}
                  data-testid="back-to-assessment"
                  className="kawaii-btn text-xs px-3 py-1 rounded-full bg-white border border-kawaii-border text-kawaii-text font-body font-semibold"
                >
                  ← Re-answer
                </button>
                <button
                  onClick={() => setEditing(!editing)}
                  data-testid="edit-script-btn"
                  className={`kawaii-btn text-xs px-3 py-1 rounded-full font-body font-semibold inline-flex items-center gap-1 ${
                    editing ? 'bg-kawaii-pink text-white' : 'bg-white border border-kawaii-border text-kawaii-text'
                  }`}
                >
                  <Edit3 className="w-3 h-3" strokeWidth={2.5} /> {editing ? "Done editing" : "Edit"}
                </button>
              </div>

              {editing ? (
                <textarea
                  ref={scriptRef}
                  value={script}
                  onChange={(e) => setScript(e.target.value)}
                  data-testid="emergency-script-textarea"
                  rows={18}
                  className="w-full rounded-2xl border border-kawaii-border bg-white p-4 font-body text-[14px] text-kawaii-text leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#E63946]/60"
                />
              ) : (
                <div
                  data-testid="emergency-script-text"
                  className="rounded-2xl border border-kawaii-border bg-kawaii-bg/50 p-5 font-body text-[15px] text-kawaii-text leading-relaxed whitespace-pre-wrap max-h-[50vh] overflow-y-auto"
                >
                  {script}
                </div>
              )}

              <div className="rounded-2xl border border-kawaii-border bg-white p-3">
                <div className="text-[10px] uppercase tracking-[0.25em] text-kawaii-textSoft font-bold mb-1.5">
                  Save custom notes (added to future scripts)
                </div>
                <textarea
                  value={addendum}
                  onChange={(e) => setAddendum(e.target.value)}
                  rows={2}
                  placeholder="e.g. My port is on the right chest wall. Last IV flucloxacillin caused a rash."
                  data-testid="script-addendum-input"
                  className="w-full rounded-2xl border border-kawaii-border bg-kawaii-bg/40 px-3 py-2 font-body text-xs text-kawaii-text focus:outline-none focus:ring-2 focus:ring-kawaii-pink resize-none"
                />
                <div className="flex justify-end mt-2 gap-2">
                  {editing && (
                    <button
                      onClick={resetToAuto}
                      data-testid="reset-script-btn"
                      className="kawaii-btn text-[11px] px-3 py-1 rounded-full bg-white border border-kawaii-border text-kawaii-text font-body font-semibold inline-flex items-center gap-1"
                    >
                      <RotateCcw className="w-3 h-3" strokeWidth={2.5} /> Reset to auto
                    </button>
                  )}
                  <button
                    onClick={saveAddendum}
                    disabled={savingAddendum}
                    data-testid="save-addendum-btn"
                    className="kawaii-btn text-[11px] px-3 py-1 rounded-full bg-kawaii-pink text-white font-body font-semibold inline-flex items-center gap-1 disabled:opacity-60"
                  >
                    {savingAddendum ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" strokeWidth={2.5} />}
                    Save notes
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <button
                  onClick={copy} data-testid="copy-script-btn"
                  className="kawaii-btn px-3 py-2.5 rounded-2xl bg-gradient-to-r from-[#FF6B6B] to-[#E63946] text-white font-body font-semibold text-xs shadow-sm inline-flex items-center justify-center gap-2"
                >
                  <Copy className="w-3.5 h-3.5" strokeWidth={2.5} /> Copy
                </button>
                <button
                  onClick={speaking ? stopSpeaking : speak}
                  data-testid="speak-script-btn"
                  className={`kawaii-btn px-3 py-2.5 rounded-2xl font-body font-semibold text-xs inline-flex items-center justify-center gap-2 ${
                    speaking ? 'bg-[#E63946] text-white' : 'bg-white border border-kawaii-border text-kawaii-text'
                  }`}
                >
                  {speaking ? <VolumeX className="w-3.5 h-3.5" strokeWidth={2.5} /> : <Volume2 className="w-3.5 h-3.5" strokeWidth={2.5} />}
                  {speaking ? "Stop" : "Read aloud"}
                </button>
                <button
                  onClick={printPage} data-testid="print-script-btn"
                  className="kawaii-btn px-3 py-2.5 rounded-2xl bg-white border border-kawaii-border text-kawaii-text font-body font-semibold text-xs inline-flex items-center justify-center gap-2"
                >
                  <Printer className="w-3.5 h-3.5" strokeWidth={2.5} /> Print
                </button>
                <button
                  onClick={askLymphie} data-testid="emergency-ask-lymphie"
                  className="kawaii-btn px-3 py-2.5 rounded-2xl bg-white border border-kawaii-border text-kawaii-text font-body font-semibold text-xs inline-flex items-center justify-center gap-2"
                >
                  <MessageCircleHeart className="w-3.5 h-3.5" strokeWidth={2.5} /> Lymphie
                </button>
              </div>

              <div className="pt-4 border-t border-kawaii-border">
                <div className="flex items-center gap-2 mb-2">
                  <Phone className="w-4 h-4 text-[#E63946]" strokeWidth={2.5} />
                  <span className="text-[10px] uppercase tracking-[0.25em] text-kawaii-textSoft font-bold">Emergency numbers</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {EMERGENCY_NUMBERS.map((e) => (
                    <a
                      key={e.number} href={`tel:${e.number}`}
                      data-testid={`call-${e.number}`}
                      className="kawaii-btn rounded-2xl border border-[#E63946]/40 bg-white p-3 text-center hover:bg-[#FFE5E5]/50"
                    >
                      <div className="text-[9px] uppercase tracking-widest text-kawaii-textSoft font-bold">{e.region}</div>
                      <div className="font-heading text-2xl font-semibold text-[#E63946] mt-1">{e.number}</div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
